import React, { useRef, useState, useCallback } from "react";
import { motion, useMotionValue, useSpring, useTransform, AnimatePresence } from "framer-motion";
import {
  IdCard, Bus, BookOpen, Receipt, QrCode, ShieldCheck,
  Clock, MapPin, Utensils, Coffee, GraduationCap,
  Library, CheckCircle2, ChevronRight, Wallet, Sparkles,
  TrendingUp, Calendar, X,
} from "lucide-react";

const SERIF = '"Playfair Display", Georgia, serif';

interface StudentData {
  studentName: string;
  studentNo: string;
  photo: string;
  school: string;
  grade: string;
  class: string;
}

// ════════════════════════════════════════════════════════════════════════════
// 3D TILT HOOK
// ════════════════════════════════════════════════════════════════════════════

function useTilt(maxTilt = 12) {
  const ref = useRef<HTMLDivElement>(null);
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const sx = useSpring(x, { stiffness: 300, damping: 25 });
  const sy = useSpring(y, { stiffness: 300, damping: 25 });
  const rotateX = useTransform(sy, [-0.5, 0.5], [maxTilt, -maxTilt]);
  const rotateY = useTransform(sx, [-0.5, 0.5], [-maxTilt, maxTilt]);
  const glareX = useTransform(sx, [-0.5, 0.5], ["0%", "100%"]);
  const glareY = useTransform(sy, [-0.5, 0.5], ["0%", "100%"]);

  const onMove = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    x.set((e.clientX - r.left) / r.width - 0.5);
    y.set((e.clientY - r.top) / r.height - 0.5);
  }, [x, y]);

  const onLeave = useCallback(() => { x.set(0); y.set(0); }, [x, y]);

  return { ref, rotateX, rotateY, glareX, glareY, onMove, onLeave };
}

// ════════════════════════════════════════════════════════════════════════════
// STYLIZED QR
// ════════════════════════════════════════════════════════════════════════════

function StylizedQR({ color, size = 64 }: { color: string; size?: number }) {
  const cells = 7;
  const cellSize = size / cells;
  const pattern = [
    [1,1,1,0,1,0,1], [1,0,1,1,0,1,0], [1,1,0,1,1,1,1],
    [0,1,1,0,1,0,1], [1,0,1,1,0,1,0], [1,1,0,1,1,0,1], [0,1,1,0,1,1,1],
  ];
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="rounded-xl shrink-0">
      <rect width={size} height={size} rx={8} fill="white" fillOpacity={0.96} />
      {pattern.map((row, ry) => row.map((on, cx) => on ? (
        <rect key={`${ry}-${cx}`} x={cx*cellSize+1.5} y={ry*cellSize+1.5} width={cellSize-3} height={cellSize-3} rx={1.5} fill={color} />
      ) : null))}
      {[[0,0],[0,4],[4,0]].map(([fx,fy]) => (
        <g key={`${fx}-${fy}`}>
          <rect x={fx*cellSize} y={fy*cellSize} width={cellSize*3} height={cellSize*3} rx={5} fill={color} fillOpacity={0.2} />
          <rect x={fx*cellSize+cellSize*0.5} y={fy*cellSize+cellSize*0.5} width={cellSize*2} height={cellSize*2} rx={4} fill={color} />
          <rect x={fx*cellSize+cellSize} y={fy*cellSize+cellSize} width={cellSize} height={cellSize} rx={1.5} fill="white" />
        </g>
      ))}
    </svg>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// SERVICE DEFINITIONS
// ════════════════════════════════════════════════════════════════════════════

interface ServiceDef {
  id: string;
  label: string;
  icon: React.ComponentType<{ className?: string; style?: React.CSSProperties }>;
  accent: string;
  detail: { title: string; value: string; sub: string };
}

const SERVICES: ServiceDef[] = [
  {
    id: "id",
    label: "Student ID",
    icon: IdCard,
    accent: "#0B3D2E",
    detail: { title: "Student Identity", value: "Active · Valid Dec 2026", sub: "Greenhill Academy · Primary 6A" },
  },
  {
    id: "bus",
    label: "Bus Pass",
    icon: Bus,
    accent: "#E8760C",
    detail: { title: "Route B-12", value: "Pickup 6:45 AM · Kira Rd", sub: "Term 3 · 2025" },
  },
  {
    id: "library",
    label: "Library",
    icon: BookOpen,
    accent: "#5C2D72",
    detail: { title: "3 Books Borrowed", value: "1 Due Friday · 47 Returned", sub: "Member since 2024" },
  },
  {
    id: "meal",
    label: "Meal Card",
    icon: Receipt,
    accent: "#C4371E",
    detail: { title: "UGX 45,000 Balance", value: "Full Board · 72% Used Today", sub: "Breakfast + Lunch active" },
  },
];

// ════════════════════════════════════════════════════════════════════════════
// MAIN: Single Stunning Digital Wallet Card
// ════════════════════════════════════════════════════════════════════════════

export function DigitalWalletCards({ studentName, studentNo, photo, school, grade, class: classLabel }: {
  studentName: string;
  studentNo: string;
  photo: string;
  school: string;
  grade: string;
  class: string;
}) {
  const data: StudentData = { studentName, studentNo, photo, school, grade, class: classLabel };
  const tilt = useTilt(8);
  const [activeService, setActiveService] = useState<string | null>(null);
  const [showQR, setShowQR] = useState(false);

  const deepNavy = "#0A1628";
  const midBlue = "#1B3A5C";
  const gold = "#D4A943";
  const goldLight = "#E8C768";
  const iceBlue = "#E8F0FF";

  const selectedService = SERVICES.find(s => s.id === activeService);

  return (
    <div className="relative">
      {/* ── Main Card ── */}
      <motion.div
        style={{ perspective: 1400 }}
        className="cursor-pointer select-none"
        onClick={() => setShowQR(true)}
      >
        <motion.div
          ref={tilt.ref}
          onMouseMove={tilt.onMove}
          onMouseLeave={tilt.onLeave}
          style={{ rotateX: tilt.rotateX, rotateY: tilt.rotateY, transformStyle: "preserve-3d" }}
          className="relative w-full rounded-[28px] overflow-hidden shadow-2xl"
          whileHover={{ scale: 1.01 }}
          transition={{ type: "spring", stiffness: 400, damping: 30 }}
        >
          {/* Background layers */}
          <div className="absolute inset-0" style={{ background: `linear-gradient(135deg, ${deepNavy} 0%, ${midBlue} 45%, ${deepNavy} 100%)` }} />

          {/* Mesh gradient glow */}
          <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full opacity-20 blur-3xl" style={{ background: `radial-gradient(circle, ${gold}, transparent 70%)` }} />
          <div className="absolute -bottom-24 -left-16 w-64 h-64 rounded-full opacity-15 blur-3xl" style={{ background: `radial-gradient(circle, ${midBlue}, transparent 70%)` }} />

          {/* Geometric pattern */}
          <svg className="absolute inset-0 w-full h-full opacity-[0.04]" preserveAspectRatio="none" viewBox="0 0 100 100">
            <defs>
              <pattern id="hexgrid" x="0" y="0" width="10" height="8.66" patternUnits="userSpaceOnUse">
                <polygon points="5,0 9.33,2.5 9.33,7.5 5,10 0.67,7.5 0.67,2.5" fill="none" stroke="white" strokeWidth="0.3" />
              </pattern>
            </defs>
            <rect width="100" height="100" fill="url(#hexgrid)" />
          </svg>

          {/* Gold top accent */}
          <div className="absolute top-0 left-0 right-0 h-[3px]" style={{ background: `linear-gradient(90deg, transparent, ${gold}, ${goldLight}, ${gold}, transparent)` }} />

          {/* ── Card content ── */}
          <div className="relative z-10 p-6 sm:p-8 lg:p-10">
            {/* Header row */}
            <div className="flex items-start justify-between mb-6 sm:mb-8">
              <div className="flex items-center gap-3">
                <div className="h-12 w-12 sm:h-14 sm:w-14 rounded-2xl flex items-center justify-center shrink-0" style={{ background: `linear-gradient(135deg, ${gold}, ${goldLight})`, boxShadow: `0 4px 20px ${gold}40` }}>
                  <Wallet className="h-6 w-6 sm:h-7 sm:w-7" style={{ color: deepNavy }} />
                </div>
                <div>
                  <p className="text-[10px] sm:text-[11px] font-bold uppercase tracking-[0.2em]" style={{ color: `${gold}CC` }}>TriSphere Wallet</p>
                  <p className="text-lg sm:text-xl font-black text-white" style={{ fontFamily: SERIF }}>Digital Campus Pass</p>
                </div>
              </div>
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-full" style={{ background: "rgba(255,255,255,0.1)", backdropFilter: "blur(12px)", border: "1px solid rgba(255,255,255,0.15)" }}>
                <ShieldCheck className="h-4 w-4" style={{ color: gold }} />
                <span className="text-[10px] sm:text-[11px] font-bold uppercase tracking-wider text-white/90">Verified</span>
              </div>
            </div>

            {/* Student identity block */}
            <div className="flex items-center gap-4 sm:gap-5 mb-6 sm:mb-8">
              <div className="relative shrink-0">
                <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl overflow-hidden border-2" style={{ borderColor: `${gold}60`, boxShadow: "0 8px 24px rgba(0,0,0,0.4)" }}>
                  <img src={data.photo} alt={data.studentName} className="w-full h-full object-cover"
                    onError={(e) => { (e.target as HTMLImageElement).src = `https://ui-avatars.com/api/?name=${encodeURIComponent(data.studentName)}&background=0A1628&color=D4A943&size=200`; }}
                  />
                </div>
                <div className="absolute -bottom-1.5 -right-1.5 h-6 w-6 sm:h-7 sm:w-7 rounded-full flex items-center justify-center" style={{ background: gold, boxShadow: "0 2px 8px rgba(0,0,0,0.4)" }}>
                  <CheckCircle2 className="h-3.5 w-3.5 sm:h-4 sm:w-4" style={{ color: deepNavy }} />
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xl sm:text-2xl lg:text-3xl font-black text-white truncate" style={{ fontFamily: SERIF }}>{data.studentName}</p>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1">
                  <span className="text-xs sm:text-sm font-bold text-white/70">{data.grade} · {data.class}</span>
                  <span className="hidden sm:inline text-white/20">|</span>
                  <span className="text-[11px] sm:text-xs font-bold font-mono text-white/50 tracking-wider">{data.studentNo}</span>
                </div>
                <p className="text-[11px] sm:text-xs font-semibold text-white/40 mt-0.5 truncate">{data.school}</p>
              </div>
            </div>

            {/* Service tiles grid */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 sm:gap-3">
              {SERVICES.map((svc, i) => (
                <motion.button
                  key={svc.id}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.15 + i * 0.08 }}
                  onClick={(e) => { e.stopPropagation(); setActiveService(svc.id); }}
                  className="group relative overflow-hidden rounded-2xl p-3 sm:p-4 text-left transition-all"
                  style={{ background: "rgba(255,255,255,0.06)", backdropFilter: "blur(8px)", border: "1px solid rgba(255,255,255,0.08)" }}
                  whileHover={{ y: -2, background: "rgba(255,255,255,0.1)" }}
                >
                  {/* accent glow on hover */}
                  <div className="absolute -top-6 -right-6 w-16 h-16 rounded-full opacity-0 group-hover:opacity-30 blur-xl transition-opacity" style={{ background: svc.accent }} />
                  <div className="relative flex items-center gap-2.5 sm:flex-col sm:items-start sm:gap-2">
                    <div className="h-9 w-9 sm:h-10 sm:w-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: `${svc.accent}30`, border: `1px solid ${svc.accent}50` }}>
                      <svc.icon className="h-4.5 w-4.5 sm:h-5 sm:w-5" style={{ color: svc.accent === "#0B3D2E" ? "#1A6B52" : svc.accent === "#5C2D72" ? "#C084D8" : svc.accent }} />
                    </div>
                    <div className="min-w-0">
                      <p className="text-[11px] sm:text-xs font-bold text-white/90 truncate">{svc.label}</p>
                      <p className="text-[9px] sm:text-[10px] font-semibold text-white/40 truncate hidden sm:block">{svc.detail.value.split("·")[0].trim()}</p>
                    </div>
                  </div>
                  <ChevronRight className="absolute top-3 right-3 h-3.5 w-3.5 text-white/20 group-hover:text-white/50 group-hover:translate-x-0.5 transition-all" />
                </motion.button>
              ))}
            </div>

            {/* Footer row */}
            <div className="flex items-center justify-between mt-6 sm:mt-8 pt-4 sm:pt-5 border-t" style={{ borderColor: "rgba(255,255,255,0.08)" }}>
              <div className="flex items-center gap-2">
                <div className="h-2 w-2 rounded-full animate-pulse" style={{ background: "#C4A882", boxShadow: "0 0 8px rgba(52,199,89,0.6)" }} />
                <span className="text-[11px] sm:text-xs font-bold text-white/60">All services active</span>
              </div>
              <div className="flex items-center gap-1.5">
                <Sparkles className="h-3.5 w-3.5" style={{ color: `${gold}CC` }} />
                <span className="text-[10px] sm:text-[11px] font-bold uppercase tracking-wider" style={{ color: `${gold}CC` }}>Tap to present QR</span>
              </div>
            </div>
          </div>

          {/* Holographic glare overlay */}
          <motion.div className="pointer-events-none absolute inset-0 rounded-[28px] overflow-hidden" style={{ opacity: 0.18 }}>
            <motion.div className="absolute inset-0" style={{
              background: `radial-gradient(circle at ${tilt.glareY} ${tilt.glareX}, rgba(255,255,255,0.9) 0%, transparent 45%)`,
            }} />
          </motion.div>
        </motion.div>
      </motion.div>

      {/* Card label */}
      <div className="flex items-center gap-2 mt-4">
        <Wallet className="h-4 w-4" style={{ color: deepNavy }} />
        <span className="text-sm font-bold" style={{ color: "#1C1917" }}>Digital Wallet</span>
        <span className="text-[11px] font-semibold ml-auto" style={{ color: "#86868B" }}>4 services · Tap a tile to expand</span>
      </div>

      {/* ── Service Detail Drawer (inline expansion) ── */}
      <AnimatePresence>
        {selectedService && (
          <motion.div
            initial={{ opacity: 0, height: 0, marginTop: 0 }}
            animate={{ opacity: 1, height: "auto", marginTop: 16 }}
            exit={{ opacity: 0, height: 0, marginTop: 0 }}
            transition={{ type: "spring", stiffness: 400, damping: 35 }}
            className="overflow-hidden"
          >
            <div className="rounded-[24px] p-5 sm:p-6 shadow-xl" style={{ background: `linear-gradient(135deg, ${selectedService.accent}08, ${selectedService.accent}03)`, border: `1px solid ${selectedService.accent}25` }}>
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="h-11 w-11 rounded-2xl flex items-center justify-center" style={{ background: `${selectedService.accent}20`, border: `1px solid ${selectedService.accent}40` }}>
                    <selectedService.icon className="h-5.5 w-5.5" style={{ color: selectedService.accent === "#0B3D2E" ? "#1A6B52" : selectedService.accent === "#5C2D72" ? "#C084D8" : selectedService.accent }} />
                  </div>
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-wider" style={{ color: `${selectedService.accent}CC` }}>{selectedService.label}</p>
                    <p className="text-base sm:text-lg font-black" style={{ color: "#1C1917", fontFamily: SERIF }}>{selectedService.detail.title}</p>
                  </div>
                </div>
                <button onClick={() => setActiveService(null)} className="h-8 w-8 rounded-xl flex items-center justify-center hover:bg-black/5 transition">
                  <X className="h-4 w-4" style={{ color: "#86868B" }} />
                </button>
              </div>
              <div className="flex items-center justify-between gap-4">
                <div className="flex-1">
                  <p className="text-sm font-bold" style={{ color: "#1C1917" }}>{selectedService.detail.value}</p>
                  <p className="text-xs font-semibold mt-0.5" style={{ color: "#86868B" }}>{selectedService.detail.sub}</p>
                  <div className="flex gap-2 mt-3">
                    <button className="px-4 py-2 rounded-xl text-xs font-bold text-white" style={{ background: selectedService.accent === "#0B3D2E" ? "#1A6B52" : selectedService.accent === "#5C2D72" ? "#7B3A99" : selectedService.accent }}>
                      Present QR
                    </button>
                    <button className="px-4 py-2 rounded-xl text-xs font-bold border" style={{ borderColor: "#E2D6C0", color: "#1C1917" }}>
                      View Details
                    </button>
                  </div>
                </div>
                <StylizedQR color={selectedService.accent === "#0B3D2E" ? "#1A6B52" : selectedService.accent === "#5C2D72" ? "#7B3A99" : selectedService.accent} size={72} />
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Full QR Present Modal ── */}
      <AnimatePresence>
        {showQR && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            style={{ background: "rgba(0,0,0,0.6)", backdropFilter: "blur(20px)" }}
            onClick={() => setShowQR(false)}
          >
            <motion.div
              initial={{ scale: 0.9, y: 20, opacity: 0 }}
              animate={{ scale: 1, y: 0, opacity: 1 }}
              exit={{ scale: 0.9, y: 20, opacity: 0 }}
              transition={{ type: "spring", stiffness: 400, damping: 32 }}
              className="w-full max-w-sm rounded-[28px] overflow-hidden shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="relative p-6 sm:p-8 text-center" style={{ background: `linear-gradient(135deg, ${deepNavy}, ${midBlue})` }}>
                <button onClick={() => setShowQR(false)} className="absolute top-4 right-4 h-9 w-9 rounded-xl flex items-center justify-center" style={{ background: "rgba(255,255,255,0.1)" }}>
                  <X className="h-4 w-4 text-white/70" />
                </button>
                <div className="flex items-center justify-center gap-2 mb-1">
                  <Wallet className="h-5 w-5" style={{ color: gold }} />
                  <p className="text-[11px] font-bold uppercase tracking-[0.2em]" style={{ color: `${gold}CC` }}>TriSphere Wallet</p>
                </div>
                <p className="text-xl font-black text-white mb-1" style={{ fontFamily: SERIF }}>{data.studentName}</p>
                <p className="text-xs font-bold text-white/50 mb-6">{data.school} · {data.grade}</p>
                <div className="inline-block p-4 rounded-3xl" style={{ background: "rgba(255,255,255,0.08)", backdropFilter: "blur(8px)" }}>
                  <StylizedQR color={gold} size={160} />
                </div>
                <p className="text-sm font-bold text-white/80 mt-5">Present this QR to any campus service</p>
                <p className="text-[11px] font-semibold text-white/40 mt-1">Auto-rotates every 30s for security</p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
